import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { today } from '../lib/format';
import { Toast } from '../ui/common';

const KINDS = [
  { key: 'income', table: 'income', label: 'Income Transaction' },
  { key: 'expense', table: 'expenses', label: 'Expense Transaction' },
];

const blank = {
  date: today(),
  category: '',
  customClient: '',
  account: '',
  amount: '',
  description: '',
  remarks: '',     // For income: free text. For expense: vendor name (Paid To)
  vendor: '',      // Selected vendor name for expenses
  newVendorName: '', // Quick-add vendor input
};

export default function Add({ config, onSaved }) {
  const [kind, setKind] = useState('income');
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState('');

  // Vendors (for Expense "Vendor / Paid To" dropdown)
  const [vendors, setVendors] = useState([]);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddBusy, setQuickAddBusy] = useState(false);
  const [quickAddErr, setQuickAddErr] = useState('');
  const newVendorRef = useRef(null);

  const loadVendors = () => {
    api.get('/api/vendors')
      .then((data) => setVendors((data || []).filter((v) => v.status !== 'Inactive')))
      .catch(() => {});
  };

  useEffect(loadVendors, []);

  useEffect(() => {
    if (showQuickAdd && newVendorRef.current) {
      newVendorRef.current.focus();
    }
  }, [showQuickAdd]);

  const activeClients = kind === 'income'
    ? config.categories?.income || []
    : config.categories?.expense || [];

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setVal = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const isNewClient = f.category === '+ Add New Client' || f.category === '+ Add New Expense Category';
  const isExpense = kind === 'expense';

  // Quick Add vendor inline
  const handleQuickAddVendor = async () => {
    if (!f.newVendorName.trim()) { setQuickAddErr('Enter vendor name'); return; }
    setQuickAddBusy(true); setQuickAddErr('');
    try {
      const res = await api.post('/api/vendors', { name: f.newVendorName.trim(), status: 'Active' });
      const newVendor = res.vendor;
      loadVendors();
      setVal('vendor', newVendor.name);
      setVal('newVendorName', '');
      setShowQuickAdd(false);
      setToast(`Vendor "${newVendor.name}" added`);
    } catch (ex) {
      // If vendor already exists (409), just select it
      if (ex.message && ex.message.includes('already exists')) {
        setVal('vendor', f.newVendorName.trim());
        setVal('newVendorName', '');
        setShowQuickAdd(false);
      } else {
        setQuickAddErr(ex.message || 'Failed to add vendor');
      }
    } finally {
      setQuickAddBusy(false);
    }
  };

  const submit = async () => {
    setError(null);
    const table = KINDS.find((k) => k.key === kind).table;
    const finalCategoryName = isNewClient ? f.customClient.trim() : f.category;

    // For expense: remarks = vendor name (Paid To). For income: remarks = free text.
    const remarksValue = isExpense ? f.vendor : f.remarks;

    const payload = {
      date: f.date,
      category: finalCategoryName,
      account: f.account,
      amount: Number(f.amount),
      tax: 0,
      shipping: 0,
      fees: 0,
      description: f.description,
      remarks: remarksValue,
    };

    setBusy(true);
    try {
      await api.post(`/api/${table}`, payload);


      setToast('Transaction saved successfully');
      setF((p) => ({ ...blank, date: p.date, category: p.category, account: p.account, vendor: p.vendor }));
      onSaved && onSaved();
      window.dispatchEvent(new Event('syncSheet'));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const canSave = f.date && f.account && f.amount !== '' &&
    (f.category && (!isNewClient || f.customClient.trim()));

  return (
    <div>
      {toast && <Toast message={toast} onDone={() => setToast('')} />}
      {error && <div className="err" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Segment Selector */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {KINDS.map((k) => (
          <button
            key={k.key}
            type="button"
            style={{
              padding: '12px 24px',
              borderRadius: 12,
              border: kind === k.key ? 'none' : '1px solid #cbd5e1',
              background: kind === k.key
                ? k.key === 'income' ? '#224eae' : '#0f172a'
                : '#ffffff',
              color: kind === k.key ? '#ffffff' : '#475569',
              fontWeight: 800,
              fontSize: '0.95rem',
              cursor: 'pointer',
              boxShadow: kind === k.key ? '0 4px 14px rgba(0,0,0,0.15)' : 'none',
              transition: 'all 0.2s ease',
            }}
            onClick={() => {
              setKind(k.key);
              setError(null);
              setF((p) => ({ ...p, category: '', customClient: '', vendor: '' }));
              setShowQuickAdd(false);
            }}
          >
            {kind === k.key ? '✓ ' : ''}
            {k.label}
          </button>
        ))}
      </div>

      <div className="cm-form-section-card">
        <div className="cm-form-section-title">
          {kind === 'income' ? 'Income Transaction Details' : 'Expense Transaction Details'}
        </div>

        <div className="cm-grid-2">
          <div className="cm-field-group">
            <label>Transaction Date *</label>
            <input type="date" value={f.date} onChange={set('date')} />
          </div>

          <div className="cm-field-group">
            <label>Payment Method / Account *</label>
            <select value={f.account} onChange={set('account')}>
              <option value="">Select bank or cash account…</option>
              {config.accounts?.map((a) => (
                <option key={a.name} value={a.name}>{a.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="cm-grid-2">
          <div className="cm-field-group">
            <label>{kind === 'income' ? 'Client / Payer *' : 'Expense Category *'}</label>
            <SearchableSelect
              value={f.category}
              options={activeClients}
              placeholder={kind === 'income' ? 'Select client…' : 'Select expense category…'}
              onChange={(val) => setF((p) => ({ ...p, category: val, customClient: '' }))}
              onAddNew={() => setF((p) => ({ ...p, category: kind === 'income' ? '+ Add New Client' : '+ Add New Expense Category', customClient: '' }))}
              addNewLabel={kind === 'income' ? '+ Add New Client…' : '+ Add New Expense Category…'}
            />
          </div>

          <div className="cm-field-group">
            <label>Amount (₹) *</label>
            <input
              type="number"
              inputMode="decimal"
              placeholder="e.g. 25000"
              value={f.amount}
              onChange={set('amount')}
            />
          </div>
        </div>

        {isNewClient && (
          <div className="cm-field-group">
            <label>New Category / Client Name *</label>
            <input
              type="text"
              placeholder={kind === 'income' ? 'New client name' : 'New expense category name'}
              value={f.customClient}
              onChange={set('customClient')}
            />
          </div>
        )}

        {/* ── VENDOR / PAID TO — Expense Only ── */}
        {isExpense && (
          <div className="cm-field-group">
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Vendor / Paid To (Optional)</span>
              <button
                type="button"
                onClick={() => { setShowQuickAdd((p) => !p); setQuickAddErr(''); }}
                style={{
                  background: 'none', cursor: 'pointer',
                  color: 'var(--brand)', fontWeight: 800, fontSize: '0.8rem', padding: '2px 8px',
                  borderRadius: 6, border: '1px solid var(--line)',
                }}
              >
                {showQuickAdd ? '✕ Cancel' : '+ Add New Vendor'}
              </button>
            </label>

            {!showQuickAdd ? (
              <select value={f.vendor} onChange={set('vendor')}>
                <option value="">— Select vendor / paid to —</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.name}>{v.name}{v.category ? ` (${v.category})` : ''}</option>
                ))}
              </select>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                <div style={{ background: 'var(--chip-bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--brand)', marginBottom: 8 }}>
                    Quick Add Vendor
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      ref={newVendorRef}
                      value={f.newVendorName}
                      onChange={set('newVendorName')}
                      placeholder="Vendor name e.g. ABC Printing"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleQuickAddVendor(); } }}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="cm-btn-emerald"
                      style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                      onClick={handleQuickAddVendor}
                      disabled={quickAddBusy}
                    >
                      {quickAddBusy ? 'Adding…' : 'Add & Select'}
                    </button>
                  </div>
                  {quickAddErr && <div style={{ color: 'var(--ink)', fontSize: '0.8rem', marginTop: 6, fontWeight: 700 }}>{quickAddErr}</div>}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="cm-grid-2">
          <div className="cm-field-group">
            <label>Description / Notes</label>
            <input
              value={f.description}
              onChange={set('description')}
              placeholder="e.g. Invoice #104, Monthly ads spend"
            />
          </div>

          {/* Remarks: Only for Income (expense uses vendor field instead) */}
          {!isExpense && (
            <div className="cm-field-group">
              <label>Remarks (Optional)</label>
              <input value={f.remarks} onChange={set('remarks')} placeholder="Optional note" />
            </div>
          )}
        </div>

        {/* Vendor preview chip for expense */}
        {isExpense && f.vendor && (
          <div style={{ marginTop: 4, marginBottom: 8 }}>
            <span style={{ background: 'var(--bg)', color: 'var(--ink)', padding: '4px 12px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 800, border: '1px solid var(--line)' }}>
              💸 Paid To: {f.vendor}
            </span>
          </div>
        )}

        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="cm-btn-emerald"
            disabled={!canSave || busy}
            style={{
              background: !canSave || busy
                ? '#cbd5e1'
                : (kind === 'income' ? '#224eae' : '#0f172a'),
              color: !canSave || busy ? '#94a3b8' : '#ffffff',
              cursor: !canSave || busy ? 'not-allowed' : 'pointer',
              boxShadow: !canSave || busy ? 'none' : undefined,
              padding: '12px 28px',
              fontSize: '0.95rem',
            }}
            onClick={submit}
          >
            {busy ? 'Saving...' : `Save ${kind === 'income' ? 'Income' : 'Expense'} Entry`}
          </button>
        </div>
      </div>
    </div>
  );
}

function SearchableSelect({
  value,
  options,
  placeholder,
  onChange,
  onAddNew,
  addNewLabel = '+ Add New Entry…',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    } else {
      setSearch('');
    }
  }, [isOpen]);

  const filteredOptions = options.filter((opt) =>
    String(opt).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '12px 16px',
          borderRadius: 12,
          border: '1px solid var(--line)',
          background: 'var(--card)',
          color: value ? '#0f172a' : '#94a3b8',
          outline: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: value ? 800 : 500,
          boxShadow: isOpen ? '0 0 0 3px rgba(5, 150, 105, 0.15)' : 'none',
          borderColor: isOpen ? '#224eae' : '#cbd5e1',
        }}
      >
        <span>{value || placeholder}</span>
        <span style={{ fontSize: '0.8rem', color: 'var(--muted)', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>
          ▼
        </span>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 1000,
            background: 'var(--card)',
            border: '1px solid var(--line)',
            borderRadius: 12,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            maxHeight: 280,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: 8, borderBottom: '1px solid var(--line)' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--line)',
                fontSize: '0.9rem',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '10px 16px', color: '#94a3b8', fontSize: '0.9rem', textAlign: 'center' }}>
                No options found
              </div>
            ) : (
              filteredOptions.map((opt) => (
                <div
                  key={opt}
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    fontSize: '0.95rem',
                    color: 'var(--ink)',
                    cursor: 'pointer',
                    background: value === opt ? '#f0f4ff' : 'transparent',
                    fontWeight: value === opt ? 800 : 500,
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => {
                    if (value !== opt) e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    if (value !== opt) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {opt}
                </div>
              ))
            )}

            <div style={{ borderTop: '1px solid #f1f5f9' }} />

            <div
              onClick={() => {
                onAddNew();
                setIsOpen(false);
              }}
              style={{
                padding: '10px 16px',
                fontSize: '0.95rem',
                color: 'var(--brand)',
                fontWeight: 800,
                cursor: 'pointer',
                background: 'var(--chip-bg)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f4ff'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#f0f4ff'}
            >
              {addNewLabel}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
